import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import SafeIcon from '../components/common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiPlus, FiHeart, FiMessageCircle, FiShare2, FiMoreHorizontal, FiTrash2, FiRefreshCw, FiUser, FiCalendar, FiWind } = FiIcons;

const HERO_IMAGE = "https://cdn.midjourney.com/28f02394-adfc-4f98-83d6-1630d298c31c/0_0.webp";
const STORAGE_KEY = 'weedList.v2';

const EMOTIONS = [
  { key: 'anger', label: 'Anger', color: 'bg-red-100 text-red-700 border-red-200' },
  { key: 'sadness', label: 'Sadness', color: 'bg-blue-100 text-blue-700 border-blue-200' },
  { key: 'anxiety', label: 'Anxiety', color: 'bg-amber-100 text-amber-700 border-amber-200' },
  { key: 'fear', label: 'Fear', color: 'bg-purple-100 text-purple-700 border-purple-200' },
  { key: 'frustration', label: 'Frustration', color: 'bg-orange-100 text-orange-700 border-orange-200' },
  { key: 'doubt', label: 'Doubt', color: 'bg-gray-100 text-gray-700 border-gray-200' }
];

const REFRAMING_PROMPTS = [
  { text: "What evidence challenges this thought?" },
  { text: "How can this become a growth opportunity?" },
  { text: "What would you tell a friend thinking this?" },
  { text: "Is this thought helpful or harmful right now?" },
  { text: "What's a more balanced perspective?" }
];

export default function WeedList() {
  const [weeds, setWeeds] = useState([]);
  const [weedText, setWeedText] = useState('');
  const [selectedEmotion, setSelectedEmotion] = useState('anger');
  const [showComposer, setShowComposer] = useState(false);
  const [likedWeeds, setLikedWeeds] = useState(new Set());
  const [toast, setToast] = useState(null);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const dropdownRef = useRef();

  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const data = JSON.parse(saved);
        setWeeds(data.weeds || []);
        setLikedWeeds(new Set(data.likedWeeds || []));
      } catch (e) {
        console.error('Failed to parse weed list data:', e);
      }
    }
  }, []);

  useEffect(() => {
    const data = { weeds, likedWeeds: Array.from(likedWeeds) };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  }, [weeds, likedWeeds]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    };
    if (activeDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [activeDropdown]);

  const showToast = (message) => {
    setToast(message);
    setTimeout(() => setToast(null), 3000);
  };

  const handleAddWeed = () => {
    const trimmedText = weedText.trim();
    if (!trimmedText) return;

    const selectedEmotionData = EMOTIONS.find(e => e.key === selectedEmotion);
    const newWeed = {
      id: Date.now().toString(),
      text: trimmedText,
      emotion: selectedEmotion,
      emotionData: selectedEmotionData,
      timestamp: Date.now(),
      likes: 0,
      comments: Math.floor(Math.random() * 3) + 1,
      shares: 0
    };

    setWeeds(prev => [newWeed, ...prev]);
    setWeedText('');
    setShowComposer(false);
    showToast('Thought shared to your feed');
  };

  const handleLike = (id) => {
    const newLiked = new Set(likedWeeds);
    if (newLiked.has(id)) {
      newLiked.delete(id);
      setWeeds(prev => prev.map(weed => weed.id === id ? { ...weed, likes: weed.likes - 1 } : weed));
    } else {
      newLiked.add(id);
      setWeeds(prev => prev.map(weed => weed.id === id ? { ...weed, likes: weed.likes + 1 } : weed));
    }
    setLikedWeeds(newLiked);
  };

  const handleDelete = (id, event) => {
    event.stopPropagation();
    if (window.confirm('Delete this thought? This cannot be undone.')) {
      setWeeds(prev => prev.filter(weed => weed.id !== id));
      setActiveDropdown(null);
      showToast('Thought removed from feed');
    }
  };

  const formatTimeAgo = (timestamp) => {
    const now = Date.now();
    const diff = now - timestamp;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (days > 0) return `${days}d ago`;
    if (hours > 0) return `${hours}h ago`;
    if (minutes > 0) return `${minutes}m ago`;
    return 'just now';
  };

  return (
    <div className="space-y-6" id="weedApp">
      {/* Hero Header */}
      <div className="relative h-48 lg:h-64 rounded-3xl overflow-hidden shadow-xl">
        <div className="absolute inset-0 bg-black/40 z-10" />
        <img 
          src={HERO_IMAGE} 
          alt="Weed List" 
          className="w-full h-full object-cover" 
        />
        <div className="absolute inset-0 z-20 flex flex-col justify-end p-6 lg:p-8">
          <h1 className="text-3xl lg:text-4xl font-cormorant font-bold text-white mb-2 text-shadow-sm">
            Weed List
          </h1>
          <p className="text-white/90 font-dm-sans max-w-lg">
            Identify invasive thoughts. Uproot negativity by bringing it into the light for mindful processing.
          </p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto space-y-6">
        {/* Composer Trigger */}
        {!showComposer && (
          <Card className="p-4 flex items-center gap-4 cursor-pointer hover:shadow-md transition-shadow group" onClick={() => setShowComposer(true)}>
            <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center group-hover:bg-sage/20 transition-colors">
              <SafeIcon icon={FiPlus} className="w-6 h-6 text-charcoal/40 group-hover:text-sage" />
            </div>
            <p className="text-charcoal/50 font-dm-sans text-lg">What's weighing on your mind?</p>
          </Card>
        )}

        {/* Composer */}
        <AnimatePresence>
          {showComposer && (
            <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}>
              <Card className="p-6 border-2 border-sage/20">
                <h3 className="font-cormorant font-bold text-xl text-charcoal mb-4">Identify the Weed</h3>
                <textarea
                  value={weedText}
                  onChange={(e) => setWeedText(e.target.value)}
                  placeholder="Describe the negative thought or feeling..."
                  className="w-full p-4 bg-gray-50 rounded-xl border border-gray-200 focus:ring-2 focus:ring-sage/50 resize-none min-h-[120px] mb-4 font-dm-sans"
                  autoFocus
                />
                
                <div className="mb-6">
                  <p className="text-xs font-bold text-charcoal/50 uppercase tracking-wider mb-2">Emotional Root</p>
                  <div className="flex flex-wrap gap-2">
                    {EMOTIONS.map((emotion) => (
                      <button
                        key={emotion.key}
                        onClick={() => setSelectedEmotion(emotion.key)}
                        className={`
                          px-3 py-1.5 rounded-full text-sm font-medium transition-all border
                          ${selectedEmotion === emotion.key ? `${emotion.color} shadow-sm scale-105` : 'bg-white text-charcoal/60 border-gray-200 hover:bg-gray-50'}
                        `}
                      >
                        {emotion.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex justify-end gap-3">
                  <Button variant="secondary" onClick={() => setShowComposer(false)}>Cancel</Button>
                  <Button variant="primary" onClick={handleAddWeed} disabled={!weedText.trim()}>Process Thought</Button>
                </div>
              </Card>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Reframing Prompts */}
        <Card className="p-5 bg-gradient-to-r from-sage/5 to-goldenrod/5 border border-sage/10">
          <h3 className="font-cormorant font-bold text-charcoal mb-3 flex items-center gap-2">
            <SafeIcon icon={FiRefreshCw} className="w-4 h-4 text-sage" />
            Reframing Prompts
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {REFRAMING_PROMPTS.map((prompt, index) => (
              <div key={index} className="flex items-start gap-2 text-sm text-charcoal/70 bg-white/60 p-2 rounded-lg">
                <span className="text-sage font-bold">•</span>
                <span>{prompt.text}</span>
              </div>
            ))}
          </div>
        </Card>

        {/* Feed */}
        <div className="space-y-4">
          <AnimatePresence>
            {weeds.map((weed) => (
              <motion.div
                key={weed.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                <Card className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-charcoal to-gray-600 rounded-full flex items-center justify-center text-white shadow-md">
                        <SafeIcon icon={FiUser} className="w-5 h-5" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-charcoal">You</span>
                          <span className="text-charcoal/40 text-sm">•</span>
                          <span className="text-sm text-charcoal/60">{formatTimeAgo(weed.timestamp)}</span>
                        </div>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-bold mt-1 ${weed.emotionData?.color || 'bg-gray-100 text-gray-600'}`}>
                          {weed.emotionData?.label || weed.emotion}
                        </span>
                      </div>
                    </div>
                    
                    <div className="relative" ref={activeDropdown === weed.id ? dropdownRef : null}>
                      <button 
                        onClick={(e) => { e.stopPropagation(); setActiveDropdown(activeDropdown === weed.id ? null : weed.id); }}
                        className="p-2 text-charcoal/40 hover:text-charcoal hover:bg-gray-100 rounded-full transition-colors"
                      >
                        <SafeIcon icon={FiMoreHorizontal} className="w-5 h-5" />
                      </button>
                      {activeDropdown === weed.id && (
                        <div className="absolute right-0 top-full mt-1 bg-white rounded-xl shadow-xl border border-gray-100 py-1 z-20 min-w-[140px]">
                          <button 
                            onClick={(e) => handleDelete(weed.id, e)}
                            className="w-full px-4 py-2.5 text-left text-sm text-crimson hover:bg-crimson/5 flex items-center gap-2"
                          >
                            <SafeIcon icon={FiTrash2} className="w-4 h-4" /> Uproot
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  <p className="text-charcoal/80 font-dm-sans text-lg mb-6 leading-relaxed">
                    "{weed.text}"
                  </p>

                  <div className="flex items-center gap-6 border-t border-gray-100 pt-4">
                    <button 
                      onClick={() => handleLike(weed.id)}
                      className={`flex items-center gap-2 text-sm font-medium transition-colors ${likedWeeds.has(weed.id) ? 'text-red-500' : 'text-charcoal/50 hover:text-red-500'}`}
                    >
                      <SafeIcon icon={FiHeart} className={`w-5 h-5 ${likedWeeds.has(weed.id) ? 'fill-current' : ''}`} />
                      <span>{weed.likes}</span>
                    </button>
                    <button className="flex items-center gap-2 text-sm font-medium text-charcoal/50 hover:text-skyblue transition-colors">
                      <SafeIcon icon={FiMessageCircle} className="w-5 h-5" />
                      <span>{weed.comments} Reframes</span>
                    </button>
                  </div>
                </Card>
              </motion.div>
            ))}
          </AnimatePresence>

          {weeds.length === 0 && (
            <div className="text-center py-16">
              <SafeIcon icon={FiWind} className="w-16 h-16 text-sage/30 mx-auto mb-4" />
              <h3 className="text-xl font-cormorant font-bold text-charcoal/60">The garden is clear.</h3>
              <p className="text-charcoal/40">Enjoy the peace of mind.</p>
            </div>
          )}
        </div>
      </div>

      {/* Toast Notification */}
      <AnimatePresence>
        {toast && (
          <motion.div 
            initial={{ opacity: 0, y: 50 }} 
            animate={{ opacity: 1, y: 0 }} 
            exit={{ opacity: 0, y: 50 }}
            className="fixed bottom-6 right-6 z-50 bg-charcoal text-white px-6 py-3 rounded-full shadow-lg flex items-center gap-3"
          >
            <SafeIcon icon={FiRefreshCw} className="w-4 h-4 text-sage" />
            <span className="font-dm-sans font-medium">{toast}</span>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}